import { toEnglishDigits, toPersianDigits } from '@bookora/shared';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  WALL-CLOCK TIMES — «۰۹:۳۰», not an instant
 * ════════════════════════════════════════════════════════════════════════════
 *
 * A shift is «every Saturday 09:00–17:00»: a time of day, with no date and no
 * timezone. `<TimeRange>` from the kit is for INSTANTS (an appointment at
 * 2026-09-26T05:30Z, shown on Tehran's clock), so it parses its inputs as
 * dates. Handing it «۱۰:۰۰» parsed to `Invalid Date` and it printed its «—»
 * fallback — which is how every shift on «ساعات کاری» came to read «یکشنبه —»
 * with no hours at all, on the one screen whose whole job is the hours.
 *
 * So wall-clock values get their own renderer, `ClockRange` in
 * `clock-range.tsx`. It still goes through the bidi isolate (`NumericRange`): «۰۹:۰۰–۱۷:۰۰» is two numbers around a
 * neutral dash, the same shape as the phone-number bug, and without the
 * isolate the RTL paragraph paints the end time first.
 */

/** Minutes from midnight → «۰۹:۳۰». A colon is not a bidi neutral. */
export function clockLabel(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return toPersianDigits(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
}

/** Every quarter hour of a day, 00:00 to 24:00 inclusive, as Select options. */
export const QUARTER_HOUR_OPTIONS: ReadonlyArray<{ value: number; label: string }> = Array.from(
  { length: 97 },
  (_, index) => index * 15,
).map((minute) => ({ value: minute, label: clockLabel(minute) }));

/**
 * The time Selects' search filter.
 *
 * Ninety-seven options is a long list to scroll, so the Selects are
 * searchable — and a search has to accept what a Persian keyboard types. The
 * option labels are Persian digits; a user may type «۹», «9», «09», «۹:۳۰» or
 * «0930». All of them are reduced to ASCII digits and compared against the
 * option's `HHmm`, with a single typed hour digit read as «0h».
 */
export function clockOptionMatches(input: string, minute: number): boolean {
  const typed = toEnglishDigits(input).replace(/\D+/g, '');
  if (typed === '') return true;
  const hours = String(Math.floor(minute / 60)).padStart(2, '0');
  const minutes = String(minute % 60).padStart(2, '0');
  const compact = `${hours}${minutes}`;
  const padded =
    typed.length === 1 || (typed.length === 3 && !typed.startsWith('0')) ? `0${typed}` : typed;
  return compact.startsWith(padded) || compact.startsWith(typed);
}
